import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { Button } from './Button';
import { CHECKOUT_URL, HERO_IMAGE, HERO_IMAGE_ALT } from '../data/offer';
import { trackCheckout } from '../utils/tracking';
const negatives = ['This is not a generic workplace safety ebook.', 'This is not a collection of random safety tips.', 'This is not another text-heavy manual that takes hours to transform into training material.', "And it's not a disconnected collection of resources you have to organize yourself."];
const teachingFlow = ['OPEN THE ATLAS.', 'CHOOSE THE TOPIC.', 'SHOW THE DIAGRAM.', 'EXPLAIN THE HAZARD.', 'RUN THE ACTIVITY.', 'ASSESS THE LEARNING.'];
const specializations = [{
  number: '01',
  step: 'HAZARD IDENTIFICATION & RISK ASSESSMENT',
  detail: 'Learn how to teach workplace hazard recognition, exposure, potential consequences, risk assessment principles and prevention strategies through realistic visual scenarios.'
}, {
  number: '02',
  step: 'WAREHOUSE, LOGISTICS & MATERIAL HANDLING SAFETY',
  detail: 'Explain warehouse traffic, forklift awareness, pedestrian interactions, material storage, loading areas and workplace logistics hazards.'
}, {
  number: '03',
  step: 'MACHINERY, TOOLS & HAZARDOUS ENERGY SAFETY',
  detail: 'Teach mechanical hazards, industrial machinery anatomy, moving components, machine guarding and hazardous energy awareness through detailed technical diagrams.'
}, {
  number: '04',
  step: 'FIRE, ELECTRICAL & EMERGENCY SAFETY',
  detail: 'Show fire hazards, electrical risk situations, emergency preparedness concepts, evacuation awareness and workplace prevention principles.'
}, {
  number: '05',
  step: 'FALLS, LADDERS & WORKING AT HEIGHT SAFETY',
  detail: 'Explore fall hazards, ladder-related concerns, elevated work environments, access arrangements and prevention concepts.'
}, {
  number: '06',
  step: 'PPE, CHEMICAL HAZARDS & OCCUPATIONAL HEALTH',
  detail: 'Teach protective equipment awareness, chemical hazards, exposure pathways, ergonomics and occupational health risk factors.'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function SolutionReveal() {
  return <section className="py-20 px-4 bg-gold-soft relative overflow-hidden" data-id="element-287" data-mp="2mhp6kkn0sw5s">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-gold" data-id="element-288" data-mp="1ys39q3u4et0y" />

      <div className="max-w-4xl mx-auto text-center" data-id="element-289" data-mp="1tt9xeeqkl82d">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-290" data-mp="1tuup7wli2cwj">
          <p className="text-sm md:text-base font-semibold text-forest mb-4" data-id="element-291" data-mp="22seudgmhgtcf">
            The Complete Visual Workplace Safety Training System for Professionals
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-ink" data-id="element-292" data-mp="3e83g64o03mbn">
            WORKPLACE SAFETY <span className="text-forest" data-id="element-293" data-mp="3mpchh3ltq9b5">ATLAS</span>
          </h2>
          <p className="text-base md:text-lg text-ink font-semibold mb-6" data-id="element-294" data-mp="3qs8rw717mz40">
            A Complete 6-IN-1 Visual Training & Reference System
          </p>
          <img src={HERO_IMAGE} alt={HERO_IMAGE_ALT} className="w-full max-w-sm h-auto mx-auto mb-6 drop-shadow-xl" loading="lazy" data-id="element-295" data-mp="3ctslyib69cmx" />
          <p className="text-base md:text-lg text-gold-dark font-semibold mb-10" data-id="element-296" data-mp="9br5xlvjwvlo">
            6 PROFESSIONAL VOLUMES · SIX SAFETY SPECIALIZATIONS · 5 EXCLUSIVE BONUSES
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-297" data-mp="3vqk2wl38rfnt">
          <motion.div initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="space-y-3 bg-white/60 p-6 rounded-2xl border border-line" data-id="element-298" data-mp="4jr1ph8g7gja">
            {negatives.map(statement => <div key={statement} className="flex items-start gap-3" data-id="element-299" data-mp="34lpsk3w856ra">
                <X className="text-ink/50 shrink-0 mt-1" size={18} data-id="element-300" data-mp="30i091suepfd4" />
                <p className="text-graphite/80 text-sm" data-id="element-301" data-mp="2vj6tzvzh1gau">{statement}</p>
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="bg-white p-6 rounded-2xl border-2 border-gold" data-id="element-302" data-mp="2qlneksr1exhb">
            <div className="flex items-start gap-3" data-id="element-303" data-mp="3ehsxfhignjhx">
              <Check className="text-forest shrink-0 mt-1" size={20} data-id="element-304" data-mp="3eghvzochmntz" />
              <div className="space-y-3" data-id="element-305" data-mp="39iyfsiw8c0ze">
                <p className="text-ink font-medium text-base leading-relaxed" data-id="element-306" data-mp="athi9ru2eamd">
                  WORKPLACE SAFETY ATLAS is a complete visual training and reference system designed
                  for professionals responsible for workplace safety education.
                </p>
                <p className="text-graphite text-sm leading-relaxed" data-id="element-307" data-mp="dg0dj3abtnsn">
                  Whether you&apos;re preparing employees, training industrial workers, organizing
                  internal safety programs, teaching in a technical academy or delivering professional
                  safety awareness sessions... the system brings six essential workplace safety
                  specializations together in one organized training resource.
                </p>
                <p className="text-forest font-semibold text-sm leading-relaxed" data-id="element-308" data-mp="11ajtsl5def7r">
                  Instead of starting every week with an empty lesson plan, you have professional
                  industrial illustrations, workplace hazard scenarios, technical diagrams and
                  instructor-ready resources available in one place.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-10" data-id="element-309" data-mp="1jzzvvcwpefqa">
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5" data-id="element-310" data-mp="3h2gw3trhztwd">
            {teachingFlow.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-forest" data-id="element-311" data-mp="3c5gnbb39kd1k">
                {line}
              </li>)}
          </ul>
          <p className="mt-8 text-lg md:text-xl font-bold text-ink" data-id="element-312" data-mp="3l4bscjuknh3a">
            Make Your Next Safety Training Session Easier to Prepare and More Visual to Teach.
          </p>
          <div className="flex justify-center mt-5" data-id="element-313" data-mp="3uun956zctkrl">
            <Button href={CHECKOUT_URL} onLinkClick={trackCheckout} className="text-sm sm:text-lg py-3 sm:py-4 px-5 sm:px-8" data-id="element-314" data-mp="3utvhg78vo76l">
              GET MY COMPLETE 6-IN-1 SAFETY TRAINING SYSTEM NOW!
            </Button>
          </div>
        </div>

        <div className="mt-12" data-id="element-315" data-mp="2egnb61k2k56z">
          <p className="text-forest text-sm font-semibold uppercase tracking-widest mb-5" data-id="element-316" data-mp="2aejnmek4la5c">
            The System Covers Six Essential Workplace Safety Specializations
          </p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-317" data-mp="20cprskaq410n">
            {specializations.map(item => <li key={item.step} className="flex flex-col items-center px-3 py-4 bg-white rounded-xl border border-line border-t-2 border-t-forest" data-id="element-318" data-mp="1ql1tq0jnvlq7">
                <span className="text-sm font-bold text-gold-dark mb-1" data-id="element-319" data-mp="1gmvtdndi6nng">{item.number}</span>
                <span className="text-lg md:text-xl font-display font-extrabold tracking-tight leading-tight text-forest" data-id="element-320" data-mp="1llpadps00wsd">
                  {item.step}
                </span>
                <span className="text-sm text-graphite/80 mt-1" data-id="element-321" data-mp="17o0nydia9hl7">{item.detail}</span>
              </li>)}
          </ol>
          <p className="mt-8 text-sm md:text-base font-bold tracking-widest text-ink" data-id="element-322" data-mp="3208dfbbu569l">
            SIX SPECIALIZATIONS. SIX COMPLETE ATLASES. ONE PROFESSIONAL TRAINING SYSTEM.
          </p>
        </div>
      </div>
    </section>;
}